import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import { User, GoogleUserInfo, APP_CONFIG } from '../types'

interface AuthContextType {
    user: User | null
    isLoading: boolean
    isAdmin: boolean
    signIn: () => void
    signOut: () => void
    updateUser: (updates: Partial<User>) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Decode JWT token
function parseJwt(token: string): GoogleUserInfo | null {
    try {
        const base64Url = token.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        )
        return JSON.parse(jsonPayload)
    } catch {
        return null
    }
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    const isAdmin = user?.role === 'admin' || APP_CONFIG.ADMIN_EMAILS.includes(user?.email || '')

    // Handle Google One-Tap callback
    const handleCredentialResponse = useCallback(async (response: CredentialResponse) => {
        const googleUser = parseJwt(response.credential)
        if (!googleUser) return

        try {
            // Send to backend to create/get user
            const res = await fetch(`${APP_CONFIG.API_URL}/auth/google`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ credential: response.credential })
            })

            if (res.ok) {
                const data = await res.json()
                const userData: User = data.user
                setUser(userData)
                localStorage.setItem('sm_user', JSON.stringify(userData))
                localStorage.setItem('sm_token', response.credential)
            } else {
                // Fallback: create local user for development
                const localUser: User = {
                    id: googleUser.sub,
                    google_id: googleUser.sub,
                    email: googleUser.email,
                    name: googleUser.name,
                    picture: googleUser.picture,
                    s_coins: 0,
                    loyalty_points: 0,
                    role: APP_CONFIG.ADMIN_EMAILS.includes(googleUser.email) ? 'admin' : 'customer',
                    created_at: new Date().toISOString()
                }
                setUser(localUser)
                localStorage.setItem('sm_user', JSON.stringify(localUser))
                localStorage.setItem('sm_token', response.credential)
            }
        } catch (error) {
            console.error('Auth error:', error)
            // Fallback for offline/dev
            const localUser: User = {
                id: googleUser.sub,
                google_id: googleUser.sub,
                email: googleUser.email,
                name: googleUser.name,
                picture: googleUser.picture,
                s_coins: 0,
                loyalty_points: 0,
                role: APP_CONFIG.ADMIN_EMAILS.includes(googleUser.email) ? 'admin' : 'customer',
                created_at: new Date().toISOString()
            }
            setUser(localUser)
            localStorage.setItem('sm_user', JSON.stringify(localUser))
        }
    }, [])

    // Initialize Google One-Tap
    useEffect(() => {
        // Check for existing session
        const savedUser = localStorage.getItem('sm_user')
        if (savedUser) {
            try {
                setUser(JSON.parse(savedUser))
            } catch {
                localStorage.removeItem('sm_user')
            }
        }
        setIsLoading(false)

        // Initialize Google Identity Services
        const initGoogle = () => {
            const google = (window as any).google
            if (google && APP_CONFIG.GOOGLE_CLIENT_ID) {
                google.accounts.id.initialize({
                    client_id: APP_CONFIG.GOOGLE_CLIENT_ID,
                    callback: handleCredentialResponse,
                    auto_select: true,
                    cancel_on_tap_outside: true
                })

                // Show One-Tap if not logged in
                if (!savedUser) {
                    google.accounts.id.prompt()
                }
            }
        }

        // Wait for Google script to load
        if ((window as any).google) {
            initGoogle()
        } else {
            const checkGoogle = setInterval(() => {
                if ((window as any).google) {
                    clearInterval(checkGoogle)
                    initGoogle()
                }
            }, 100)

            return () => clearInterval(checkGoogle)
        }
    }, [handleCredentialResponse])

    const signIn = useCallback(() => {
        const google = (window as any).google
        if (google && APP_CONFIG.GOOGLE_CLIENT_ID) {
            google.accounts.id.prompt()
        } else {
            console.warn('Google Identity Services not loaded or client ID not configured')
        }
    }, [])

    const signOut = useCallback(() => {
        setUser(null)
        localStorage.removeItem('sm_user')
        localStorage.removeItem('sm_token')
        const google = (window as any).google
        if (google) {
            google.accounts.id.disableAutoSelect()
        }
    }, [])

    const updateUser = useCallback((updates: Partial<User>) => {
        setUser(prev => {
            if (!prev) return null
            const updated = { ...prev, ...updates }
            localStorage.setItem('sm_user', JSON.stringify(updated))
            return updated
        })
    }, [])

    return (
        <AuthContext.Provider value={{ user, isLoading, isAdmin, signIn, signOut, updateUser }}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider')
    }
    return context
}
